import { Paper } from '@material-ui/core'
import CustomField from 'elements/field/CustomField'
import ProUXButton from 'elements/proUXButtons/ProUXButton'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { FormControlLabel, Radio, RadioGroup, styled, Switch, Typography } from 'opensolar-ui'
import { ChangeEvent, default as React, useEffect, useState } from 'react'
import { BooleanInput, TextInput, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { doNotTranslate } from 'util/misc'
import { authSelectors } from '../../ducks/auth'
import DocusignCATemplateDialog from './DocusignCATemplateDialog'

type PropTypes = {
  disabled: boolean
  // mode: 'pdf' | 'html'
  // setMode: (newVal: 'pdf' | 'html') => void
}
const StyledSpan = styled('span')({
  display: 'flex',
  alignItems: 'center',
})

const DocusignSettingsSwitchWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
})

export const DocusignSettingsSection: React.FunctionComponent<PropTypes> = (props) => {
  const form = useForm()

  const org = useSelector(authSelectors.getCurrentOrg)

  const getInitialCountersignType = () => {
    let record = form.getState().values
    if (!record) return undefined
    if (record.docusign_require_sales_rep_countersignature) return 'dynamic'
    else if (record.docusign_designated_countersigner_email) return 'fixed'
    else return undefined
  }

  type Countersign = undefined | 'fixed' | 'dynamic'

  const getCountersigningEnabled = (dsConfig: any) => getInitialCountersignType() !== undefined

  const [showCATemplateDialog, setShowCATemplateDialog] = useState<boolean>(false)
  const [countersignEnabled, setCountersignEnabled] = useState<boolean>(getCountersigningEnabled(org?.docusign_config))
  const [countersignType, setCountersignType] = useState<Countersign>(getInitialCountersignType())
  const [separateDocumentsEnabled, setSeparateDocumentsEnabled] = useState<boolean>(
    form.getState().values?.docusign_return_as_separate_documents ?? false
  )

  useEffect(() => {
    if (form.getState().values.docusign_require_sales_rep_countersignature) {
      setCountersignType('dynamic')
    } else if (countersignType) {
      setCountersignType('fixed')
    }
  }, [form.getState().values, countersignType])

  useEffect(() => {
    form.change('enable_docusign_countersignature', countersignEnabled)
    if (!countersignEnabled) {
      form.change('docusign_require_sales_rep_countersignature', false)
      form.change('docusign_designated_countersigner_email', '')
      form.change('docusign_designated_countersigner_first_name', '')
      form.change('docusign_designated_countersigner_family_name', '')
    }
  }, [countersignEnabled])

  const translate = useTranslate()
  const isUSABased = useSelector(authSelectors.getCurrentOrg)?.country?.iso2 === 'US'

  const handleCountersignToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setCountersignEnabled(e.target.checked)
  }

  const handleCountersignTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value: Countersign = e.target.value as Countersign
    setCountersignType(value)
    if (value === 'dynamic') form.change('docusign_require_sales_rep_countersignature', true)
    else form.change('docusign_require_sales_rep_countersignature', false)
  }
  const handleSeparateDocumentsToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setSeparateDocumentsEnabled(e.target.checked)
    form.change('docusign_return_as_separate_documents', e.target.checked)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DocusignSettingsSwitchWrapper>
        <FormControlLabel
          label={translate('Enable DocuSign Countersigner')}
          labelPlacement="start"
          control={<Switch checked={countersignEnabled} onChange={handleCountersignToggle} disabled={props.disabled} />}
          style={{ alignSelf: 'flex-start' }}
        />
        {form.getState().errors?.enable_docusign_countersignature && (
          <p style={{ color: 'red', textAlign: 'center' }}>
            {form.getState().errors?.enable_docusign_countersignature}
          </p>
        )}
        <CustomField
          component={BooleanInput}
          name={'show_header'}
          label={'Show header (in docusign PDF)'}
          source="show_header"
          defaultValue={false}
        />
      </DocusignSettingsSwitchWrapper>

      <Paper style={{ padding: '10px', width: '100%', display: countersignEnabled ? undefined : 'none' }}>
        <Typography textVariant="body1Bold" style={{ textAlign: 'center', marginTop: '20px' }}>
          {translate('How would you like to choose the countersigner for this contract?')}
        </Typography>
        <RadioGroup
          onChange={handleCountersignTypeChange}
          style={{ display: 'flex', flexDirection: 'row' }}
          value={countersignType}
        >
          <FormControlLabel
            label={translate('Dynamic - Assigned Team Member is countersigner')}
            value="dynamic"
            control={<Radio />}
          />
          <FormControlLabel
            label={translate('Fixed - set one individual to be countersigner for all projects')}
            value="fixed"
            control={<Radio />}
          />
        </RadioGroup>
        {countersignType && (
          <>
            <p className="small">
              {countersignType === 'dynamic'
                ? translate(
                    'The information below will enable a countersignature in the unlikely event that the Assigned Team Member cannot be used as the countersigner (e.g. that user does not have a first and last name entered in OpenSolar).'
                  )
                : translate('The information below will be used to set the countersigner for every contract.')}
            </p>
            <CustomField
              component={TextInput}
              fullWidth={true}
              name="docusign_designated_countersigner_email"
              source="docusign_designated_countersigner_email"
              label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner Email`}
              disabled={props.disabled}
            />
            <CustomField
              component={TextInput}
              fullWidth={true}
              name="docusign_designated_countersigner_first_name"
              source="docusign_designated_countersigner_first_name"
              label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner First Name`}
              disabled={props.disabled}
            />
            <CustomField
              component={TextInput}
              fullWidth={true}
              name="docusign_designated_countersigner_family_name"
              source="docusign_designated_countersigner_family_name"
              label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner Last Name`}
              disabled={props.disabled}
            />
          </>
        )}
        <br />
        {isUSABased && (
          <div
            style={{
              margin: '20px 0px 50px 0px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <h4>{translate('California Consumer Protection Guide Templates')}</h4>
            <p>
              {translate('Manage regulatory required Consumer Protection Guides templates by clicking the button below.')}{' '}
              {translate('Read our')}{' '}
              <a href="https://support.opensolar.com" target="_blank" rel="noopener noreferrer">
                {translate('support article')}
              </a>{' '}
              {translate('to learn more.')}
            </p>
            <ProUXButton
              type="primary"
              label={translate('Manage Templates')}
              onClick={() => setShowCATemplateDialog(true)}
            />
            <StyledSpan>
              <FormControlLabel
                disabled={!org?.docusign_config?.enable_california_protection_guide}
                label={doNotTranslate('Return as Separate Documents')}
                labelPlacement="end"
                control={<Switch checked={separateDocumentsEnabled} onChange={handleSeparateDocumentsToggle} />}
              />
              <InfoTooltip
                title={doNotTranslate(
                  'Toggling this setting on will return the signed Consumer Protection Guide, Supporting Info and Contract documents individually. Toggle off to return one merged document.'
                )}
              />
            </StyledSpan>
            <DocusignCATemplateDialog isOpen={showCATemplateDialog} onClose={() => setShowCATemplateDialog(false)} />
          </div>
        )}
      </Paper>
    </div>
  )
}
export default DocusignSettingsSection
